import BaseController from "confinus_ui/base_controller";

interface VariantData {
  encoded_id: string;
  sell_uom: string;
  humanized_uom: string;
}

interface ProductMetadata {
  encoded_id: string;
  variant_id: string;
  sell_uom: string;
  variants: VariantData[];
}

export default class extends BaseController {
  static targets = ["itemsBody", "grandTotal", "lineTotal", "itemRow"];
  static values = {
    itemIndex: { type: Number, default: 0 },
    currency: { type: String, default: "USD" },
  };

  declare readonly itemsBodyTarget: HTMLElement;
  declare readonly grandTotalTarget: HTMLElement;
  declare readonly itemRowTargets: HTMLElement[];
  declare itemIndexValue: number;
  declare currencyValue: string;

  connect() {
    this.itemIndexValue = this.itemsBodyTarget.querySelectorAll("tr").length;
    this.recalculateGrandTotal();
  }

  productSelected(event: CustomEvent) {
    const {
      id: sku,
      label,
      metadata,
    } = event.detail as {
      id: string;
      label: string;
      metadata: ProductMetadata;
    };
    if (!sku || !metadata) return;

    this.appendItemRow(sku, label, metadata);
  }

  removeItem(event: Event) {
    const row = (event.target as HTMLElement).closest("tr");
    if (row) {
      row.remove();
      this.reindexItems();
      this.recalculateGrandTotal();
    }
  }

  updateTotal() {
    this.recalculateGrandTotal();
  }

  variantChanged(event: Event) {
    const select = event.target as HTMLSelectElement;
    const row = select.closest("tr");
    if (!row) return;

    const selectedOption = select.selectedOptions[0];
    const variantId = select.value;
    const sellUom = selectedOption?.dataset.sellUom || "";

    const variantInput = row.querySelector(
      'input[name*="[specified_product_variant_id]"]',
    ) as HTMLInputElement;
    const uomInput = row.querySelector(
      'input[name*="[uom]"]',
    ) as HTMLInputElement;

    if (variantInput) variantInput.value = variantId;
    if (uomInput) uomInput.value = sellUom;
  }

  private appendItemRow(
    sku: string,
    _label: string,
    metadata: ProductMetadata,
  ) {
    const index = this.itemIndexValue;
    const zeroCurrency = this.formatCurrency(0);

    const row = document.createElement("tr");
    row.setAttribute(
      "data-mercor--proposals--proposal-items-target",
      "itemRow",
    );

    const variants = metadata.variants || [];
    const firstVariant = variants[0];
    const selectedVariant = firstVariant;

    const skuCell = this.createCell("px-4 py-3 text-sm font-medium", sku);
    const descCell = this.createCell("px-4 py-3 text-sm", "");

    const uomCell = document.createElement("td");
    uomCell.className = "px-4 py-3 text-sm";

    if (variants.length > 1) {
      const select = document.createElement("select");
      select.className =
        "rounded-md border-gray-300 shadow-xs focus:border-primary-500 focus:ring-primary-500 sm:text-sm";
      select.setAttribute(
        "data-action",
        "change->mercor--proposals--proposal-items#variantChanged",
      );
      variants.forEach((v) => {
        const option = document.createElement("option");
        option.value = v.encoded_id;
        option.dataset.sellUom = v.sell_uom;
        option.textContent = v.humanized_uom;
        select.appendChild(option);
      });
      uomCell.appendChild(select);
    } else {
      uomCell.textContent =
        selectedVariant?.humanized_uom || metadata.sell_uom || "";
    }

    const qtyCell = document.createElement("td");
    qtyCell.className = "px-4 py-3";
    const qtyInput = document.createElement("input");
    qtyInput.type = "number";
    qtyInput.name = `order_proposal[items][${index}][quantity]`;
    qtyInput.step = "0.00001";
    qtyInput.min = "0";
    qtyInput.value = "1";
    qtyInput.className =
      "w-24 text-right rounded-md border-gray-300 shadow-xs focus:border-primary-500 focus:ring-primary-500 sm:text-sm";
    qtyInput.setAttribute(
      "data-action",
      "input->mercor--proposals--proposal-items#updateTotal",
    );
    qtyCell.appendChild(qtyInput);

    const priceCell = document.createElement("td");
    priceCell.className = "px-4 py-3";
    const priceInput = document.createElement("input");
    priceInput.type = "number";
    priceInput.name = `order_proposal[items][${index}][unit_price_dollars]`;
    priceInput.step = "0.01";
    priceInput.min = "0";
    priceInput.value = "";
    priceInput.placeholder = "0.00";
    priceInput.className =
      "w-28 text-right rounded-md border-gray-300 shadow-xs focus:border-primary-500 focus:ring-primary-500 sm:text-sm";
    priceInput.setAttribute(
      "data-action",
      "input->mercor--proposals--proposal-items#updateTotal",
    );
    priceCell.appendChild(priceInput);

    const totalCell = document.createElement("td");
    totalCell.className = "px-4 py-3 text-sm text-right font-medium";
    totalCell.setAttribute(
      "data-mercor--proposals--proposal-items-target",
      "lineTotal",
    );
    totalCell.textContent = zeroCurrency;

    const removeCell = document.createElement("td");
    removeCell.className = "px-4 py-3 text-center";
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "text-red-600 hover:text-red-800";
    removeBtn.textContent = "Remove";
    removeBtn.setAttribute(
      "data-action",
      "mercor--proposals--proposal-items#removeItem",
    );
    removeCell.appendChild(removeBtn);

    row.appendChild(skuCell);
    row.appendChild(descCell);
    row.appendChild(uomCell);
    row.appendChild(qtyCell);
    row.appendChild(priceCell);
    row.appendChild(totalCell);
    row.appendChild(removeCell);

    this.appendHiddenInput(
      row,
      `order_proposal[items][${index}][specified_product_id]`,
      metadata.encoded_id,
    );
    this.appendHiddenInput(
      row,
      `order_proposal[items][${index}][specified_product_variant_id]`,
      selectedVariant?.encoded_id || metadata.variant_id,
    );
    this.appendHiddenInput(
      row,
      `order_proposal[items][${index}][currency_code]`,
      this.currencyValue,
    );
    this.appendHiddenInput(
      row,
      `order_proposal[items][${index}][uom]`,
      selectedVariant?.sell_uom || metadata.sell_uom || "",
    );

    this.itemsBodyTarget.appendChild(row);
    this.itemIndexValue = index + 1;
  }

  private createCell(className: string, text: string): HTMLTableCellElement {
    const td = document.createElement("td");
    td.className = className;
    td.textContent = text;
    return td;
  }

  private appendHiddenInput(parent: HTMLElement, name: string, value: string) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    parent.appendChild(input);
  }

  private reindexItems() {
    const rows = this.itemsBodyTarget.querySelectorAll("tr");
    rows.forEach((row, index) => {
      row.querySelectorAll("input[name]").forEach((input) => {
        const name = (input as HTMLInputElement).name;
        (input as HTMLInputElement).name = name.replace(
          /\[items\]\[\d+\]/,
          `[items][${index}]`,
        );
      });
    });
    this.itemIndexValue = rows.length;
  }

  private recalculateGrandTotal() {
    let grandTotal = 0;

    const rows = this.itemsBodyTarget.querySelectorAll("tr");
    rows.forEach((row) => {
      const qtyInput = row.querySelector(
        'input[name*="[quantity]"]',
      ) as HTMLInputElement;
      const priceInput = row.querySelector(
        'input[name*="[unit_price_dollars]"]',
      ) as HTMLInputElement;
      const lineTotalCell = row.querySelector(
        '[data-mercor--proposals--proposal-items-target="lineTotal"]',
      );

      const qty = parseFloat(qtyInput?.value || "0");
      const price = parseFloat(priceInput?.value || "0");
      const lineTotal = qty * price;

      if (lineTotalCell) {
        lineTotalCell.textContent = this.formatCurrency(lineTotal);
      }

      grandTotal += lineTotal;
    });

    this.grandTotalTarget.textContent = this.formatCurrency(grandTotal);
  }

  private formatCurrency(dollars: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: this.currencyValue,
    }).format(dollars);
  }
}
